import { useCallback, useEffect, useRef } from 'react';
import {
  CurrencyFormatterOptions,
  intlNumberFormatter,
  useFormattedInput,
} from 'react-formatted-input-hook';

import {
  CURRENCIES_META,
  CurrencyCodeType,
} from '@mint-lib/currencies-helpers';
import { useTranslation } from '@mint-lib/i18n';
import {
  useProfile,
  type CurrencyFormatType,
  type ProfileLocale,
} from '@mint-lib/profile-helpers';

import { useNumberLocalize } from './locales/useNumberLocalize.js';

export interface CurrencyFieldProps extends Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'color' | 'size'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  maxDecimals?: number;
  currency: CurrencyCodeType;
  color?: 'default' | 'warning' | 'error';
  size?: 'small' | 'medium';
  restFormatProps?: Omit<
    CurrencyFormatterOptions,
    'location' | 'value' | 'onChange' | 'currency'
  > & { minDecimals?: number };
  /**
   * TODO(PDT-102307): remove this prop together with the
   * `decimalFormattingOnCellEdit` feature flag.
   */
  preserveRawValueOnEdit?: boolean;
}

const CURRENCY_FORMAT_MAP: Record<ProfileLocale, CurrencyFormatType> = {
  'en-GB': 'before',
  'en-US': 'before',
  'pt-BR': 'before',
  'it-IT': 'after',
  FAKE: 'after',
};

export const useFormattedCurrencyInput = (props: CurrencyFieldProps) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    maxDecimals = 2,
    restFormatProps = { minDecimals: 2 },
    preserveRawValueOnEdit,
    currency,
    autoFocus,
    ...restProps
  } = props;

  const { i18n } = useTranslation('@mint-lib/formatting-hooks');

  const { getProfileField } = useProfile();

  const threeCapitalLetter = /^[A-Z]{3}$/g;

  if (!currency.match(threeCapitalLetter)) {
    throw Error('Currency should be in standard currency format like USD');
  }

  const currencySymbol = CURRENCIES_META[currency].symbol;

  const locale = useNumberLocalize();

  // By default BE returns null or '' if user selected auto format
  // in this case we should use locale as value for mapping
  //  it to CURRENCY_FORMAT_MAP
  const formatLocale = getProfileField('locale')
    ? getProfileField('locale')
    : i18n.language;

  const currencyFormat =
    getProfileField('formattingConfig')?.currencyFormat ||
    CURRENCY_FORMAT_MAP[formatLocale as ProfileLocale];

  const prevLocaleRef = useRef(locale);
  const prevCurrencyFormateRef = useRef(currencyFormat);

  const formattedInput = useFormattedInput(
    intlNumberFormatter({
      ...restFormatProps,
      ...(preserveRawValueOnEdit ? { minDecimals: 0 } : {}),
      locales: locale,
      value,
      onChange,
      maxDecimals,
      liveUpdate: true,
      prefix: currencyFormat === 'before' ? currencySymbol : undefined,
      suffix: currencyFormat === 'after' ? ` ${currencySymbol}` : undefined,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    onKeyDown: libOnKeyDown,
    ...rest
  } = formattedInput.props;

  // A workaround hook to reactive update formatting when user changes config
  useEffect(() => {
    if (
      (prevLocaleRef.current !== locale ||
        prevCurrencyFormateRef.current !== currencyFormat) &&
      formattedInput.props.ref.current
    ) {
      formattedInput.props.ref.current.focus();
      prevLocaleRef.current = locale;
      prevCurrencyFormateRef.current = currencyFormat;
    }
  }, [locale, currencyFormat]);

  // A workaround for the issue with the `autoFocus` prop. You can read more about it here: https://github.com/nick-keller/react-formatted-input-hook/issues/3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return {
    ref,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...rest,
    ...restProps,
  };
};
